interface MemoryManagerConfig {
  root?: Element | Document | null;
  rootMargin?: string;
  threshold?: number | number[];
  unloadThreshold?: number;
  unloadRootMargin?: string;

  enableImageUnloading?: boolean;
  enableSwiperControl?: boolean;
  enableGlobalTracking?: boolean;
  maxVisibleProducts?: number;
  cleanupThreshold?: number;
  debugMode?: boolean;
}

interface MemoryManagerState {
  isInView: boolean;
  isNearView: boolean;
}

class GlobalMemoryManager {
  private static instance: GlobalMemoryManager;
  private visibleProducts: Set<string> = new Set();
  private productElements: Map<string, HTMLElement> = new Map();
  private cleanupTimer: number | null = null;
  private cleanupDebounceTimer: number | null = null;

  private config = {
    maxVisibleProducts: this.isMobile() ? 200 : 300,
    cleanupThreshold: this.isMobile() ? 300 : 500,
    debugMode: true,
  };

  private constructor() {
    if (this.isMobile() && typeof window !== "undefined") {
      this.cleanupTimer = window.setInterval(() => {
        this.cleanupInvisibleProducts();
      }, 30000);
    }
  }

  static getInstance(): GlobalMemoryManager {
    if (!GlobalMemoryManager.instance) {
      GlobalMemoryManager.instance = new GlobalMemoryManager();
    }
    return GlobalMemoryManager.instance;
  }

  private isMobile(): boolean {
    return typeof window !== "undefined" && window.innerWidth < 768;
  }

  registerProduct(productId: string, element: HTMLElement): void {
    this.productElements.set(productId, element);
    element.setAttribute("data-product-id", productId);
  }

  unregisterProduct(productId: string): void {
    this.productElements.delete(productId);
    this.visibleProducts.delete(productId);
  }

  markVisible(productId: string, isVisible: boolean): void {
    if (isVisible) {
      this.visibleProducts.add(productId);
      const element = this.productElements.get(productId);
      if (element) {
        this.reloadProductImages(element);
      }
    } else {
      this.visibleProducts.delete(productId);
    }

    if (this.productElements.size > this.config.cleanupThreshold) {
      this.debouncedCleanup();
    }
  }

  private debouncedCleanup(): void {
    if (this.cleanupDebounceTimer) {
      clearTimeout(this.cleanupDebounceTimer);
    }

    this.cleanupDebounceTimer = window.setTimeout(() => {
      this.cleanupInvisibleProducts();
    }, 1000);
  }

  private cleanupInvisibleProducts(): void {
    if (!this.isMobile()) return;

    const invisibleProducts = Array.from(this.productElements.keys())
      .filter(id => !this.visibleProducts.has(id))
      .slice(0, -50);

    invisibleProducts.forEach(productId => {
      const element = this.productElements.get(productId);
      if (element) {
        this.unloadProductImages(element);
      }
    });

    if (this.config.debugMode) {
      console.log(
        `Memory Manager: Total products: ${this.productElements.size}, Visible: ${this.visibleProducts.size}, Unloaded images from: ${invisibleProducts.length}`,
      );
    }
  }

  private unloadProductImages(element: HTMLElement): void {
    element.querySelectorAll("img").forEach(img => {
      img.src = "";
      img.srcset = "";
    });

    element.querySelectorAll("source").forEach(source => {
      source.srcset = "";
    });

    element.querySelectorAll("video").forEach(video => {
      video.src = "";
      video.load();
    });
  }

  private reloadProductImages(element: HTMLElement): void {
    element.dispatchEvent(new CustomEvent("product-reload", { bubbles: true }));
  }

  getStats() {
    return {
      visible: this.visibleProducts.size,
      total: this.productElements.size,
      mobile: this.isMobile(),
    };
  }

  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    if (this.cleanupDebounceTimer) {
      clearTimeout(this.cleanupDebounceTimer);
    }
    this.productElements.clear();
    this.visibleProducts.clear();
  }
}

export const globalMemoryManager = GlobalMemoryManager.getInstance();

export function memoryManager(
  node: Element,
  params?: MemoryManagerConfig,
): {
  update: (newParams: MemoryManagerConfig) => void;
  destroy: () => void;
  getState: () => MemoryManagerState;
} {
  let loadObserver: IntersectionObserver | null = null;
  let unloadObserver: IntersectionObserver | null = null;
  let isInView = false;
  let isNearView = true;
  let productId: string | null = null;

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const mobileUnloadDistance = isMobile ? "200px" : "1000px";
  const mobileLoadDistance = isMobile ? "25px" : "50px";

  const defaultOptions: MemoryManagerConfig = {
    root: null,
    rootMargin: mobileLoadDistance,
    threshold: 0.1,
    unloadThreshold: 0,
    unloadRootMargin: mobileUnloadDistance,
    enableImageUnloading: true,
    enableSwiperControl: true,
    enableGlobalTracking: false,
    debugMode: false,
  };

  const options = { ...defaultOptions, ...params };

  if (options.enableGlobalTracking) {
    productId =
      node.getAttribute("data-product-id") ||
      node.getAttribute("id") ||
      `product-${Date.now()}`;
    globalMemoryManager.registerProduct(productId, node as HTMLElement);
  }

  const handleIntersect = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      const wasInView = isInView;
      isInView = entry.isIntersecting;

      if (options.debugMode) {
        console.log(
          `Element ${
            entry.target.id || "unknown"
          } - inView: ${isInView}, was: ${wasInView}`,
        );
      }

      if (options.enableGlobalTracking && productId) {
        globalMemoryManager.markVisible(productId, isInView);
      }

      if (wasInView !== isInView) {
        node.dispatchEvent(
          new CustomEvent("intersect", {
            detail: {
              isIntersecting: isInView,
              entry,
              type: "load",
            },
          }),
        );
      }
    });
  };

  const handleUnloadIntersect = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(entry => {
      const wasNearView = isNearView;
      isNearView = entry.isIntersecting;

      if (options.debugMode) {
        console.log(
          `Element ${
            entry.target.id || "unknown"
          } - nearView: ${isNearView}, was: ${wasNearView}`,
        );
      }

      if (wasNearView !== isNearView) {
        node.dispatchEvent(
          new CustomEvent("intersect", {
            detail: {
              isIntersecting: isNearView,
              entry,
              type: "unload",
            },
          }),
        );
      }
    });
  };

  const setObservers = (opts: MemoryManagerConfig) => {
    if (loadObserver) loadObserver.disconnect();
    if (unloadObserver) unloadObserver.disconnect();

    loadObserver = new IntersectionObserver(handleIntersect, {
      root: opts.root,
      rootMargin: opts.rootMargin,
      threshold: opts.threshold,
    });
    loadObserver.observe(node);

    if (opts.enableImageUnloading) {
      unloadObserver = new IntersectionObserver(handleUnloadIntersect, {
        root: opts.root,
        rootMargin: opts.unloadRootMargin,
        threshold: opts.unloadThreshold,
      });
      unloadObserver.observe(node);
    }
  };

  setObservers(options);

  return {
    update(newParams: MemoryManagerConfig) {
      const newOptions = { ...options, ...newParams };
      setObservers(newOptions);
    },

    destroy() {
      if (loadObserver) loadObserver.disconnect();
      if (unloadObserver) unloadObserver.disconnect();

      if (options.enableGlobalTracking && productId) {
        globalMemoryManager.unregisterProduct(productId);
      }
    },

    getState() {
      return {
        isInView,
        isNearView,
      };
    },
  };
}

export default memoryManager;
